import React, { useState, useEffect } from "react";
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";
import { galleryImages } from "../data/galleryImages.js";
import { getPagedImages, getTotalPages } from "../utils/galleryUtils.js";

function GalleryPage() {
  const [selectedImg, setSelectedImg] = useState(null);
  const [page, setPage] = useState(0);
  const [imagesPerPage, setImagesPerPage] = useState(8); // default for desktop

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 640) {
        setImagesPerPage(2); // mobile
      } else {
        setImagesPerPage(8); // desktop/tablet
      }
    }
    handleResize(); // set on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalPages = getTotalPages(galleryImages, imagesPerPage);
  const pagedImages = getPagedImages(galleryImages, page, imagesPerPage);

  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [imagesPerPage, totalPages, page]);

  return (
    <>
      <Header />
      <main className="gallery-page bg-gradient-to-br from-gray-50 via-cyan-50/30 to-white">
        {/* Enhanced Hero Section */}
        <section className="hero relative bg-gradient-to-br from-cyan-800 via-cyan-900 to-teal-900 text-white py-12 overflow-hidden">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-teal-200 drop-shadow-2xl animate-fade-in">
              Gallery
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-cyan-100 font-light animate-fade-in delay-300">
              Discover our healing touch through a visual journey of our spa and
              massage therapies.
            </p>
          </div>
        </section>

        {/* Enhanced Gallery Grid & Pagination */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="bg-gradient-to-br from-white via-cyan-50/30 to-white rounded-3xl shadow-2xl p-8 border border-cyan-100 hover:shadow-cyan-200/50 transition-all duration-500">
              {/* Enhanced Gallery Grid */}
              <div
                className={`grid gap-8 ${
                  imagesPerPage === 2
                    ? "grid-cols-1"
                    : "grid-cols-1 sm:grid-cols-2 md:grid-cols-4"
                }`}
              >
                {pagedImages.map((img, idx) => (
                  <div
                    key={idx}
                    className="group relative cursor-pointer rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-teal-200 hover:border-teal-400 transform hover:scale-105 hover:-translate-y-2"
                    onClick={() => setSelectedImg(img)}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={img.src}
                        alt={img.caption}
                        loading="lazy"
                        className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                      />

                      {/* Overlay effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {/* Zoom icon */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <i className="fas fa-search-plus text-white text-2xl"></i>
                        </div>
                      </div>

                      {/* Floating particles */}
                      <div className="absolute -top-2 -right-2 w-4 h-4 bg-cyan-400 rounded-full animate-ping opacity-75"></div>
                      <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-teal-400 rounded-full animate-ping opacity-75 delay-500"></div>
                    </div>

                    <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-teal-800/90 via-teal-600/70 to-transparent text-white text-sm px-4 py-3 font-medium">
                      {img.caption}
                    </div>
                  </div>
                ))}
              </div>

              {/* Enhanced Pagination Arrows */}
              <div className="flex justify-between items-center mt-10 bg-gradient-to-r from-cyan-50 to-teal-50 rounded-2xl p-6 border border-cyan-200">
                <button
                  className={`group relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full p-4 shadow-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-110 ${
                    page === 0
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:shadow-2xl"
                  }`}
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                  aria-label="Previous"
                >
                  <i className="fas fa-chevron-left text-xl transform group-hover:-translate-x-1 transition-transform duration-300"></i>
                </button>

                <div className="flex items-center space-x-4">
                  <span className="text-teal-700 font-bold text-xl bg-white px-6 py-3 rounded-full shadow-lg border border-teal-200">
                    Page {page + 1} of {totalPages}
                  </span>
                </div>

                <button
                  className={`group relative bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full p-4 shadow-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-110 ${
                    page === totalPages - 1
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:shadow-2xl"
                  }`}
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages - 1}
                  aria-label="Next"
                >
                  <i className="fas fa-chevron-right text-xl transform group-hover:translate-x-1 transition-transform duration-300"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Lightbox Modal */}
        {selectedImg && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-modal-in"
            onClick={() => setSelectedImg(null)}
          >
            <div
              className="bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl p-6 max-w-2xl w-full relative flex flex-col items-center shadow-2xl border border-gray-200 transform animate-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center w-full mb-6">
                <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-700">
                  Gallery Image
                </h3>
                <button
                  className="group w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-all duration-300 transform hover:scale-110 shadow-lg"
                  onClick={() => setSelectedImg(null)}
                  aria-label="Close"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-teal-200 mb-6">
                <img
                  src={selectedImg.src}
                  alt={selectedImg.caption}
                  className="w-full h-auto rounded-2xl"
                />

                {/* Floating particles */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-teal-400 rounded-full animate-ping opacity-75"></div>
                <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-cyan-400 rounded-full animate-ping opacity-75 delay-300"></div>
              </div>

              <div className="text-center bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-4 w-full border border-teal-200">
                <h4 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-700 to-cyan-700 mb-2">
                  {selectedImg.caption}
                </h4>
                <p className="text-gray-600 font-medium">
                  Traditional Ku Nye Massage Technique
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />

      {/* Custom styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes modal-in {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(4px);
          }
        }

        @keyframes modal-content {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-modal-in {
          animation: modal-in 0.3s ease-out forwards;
        }

        .animate-modal-content {
          animation: modal-content 0.3s ease-out forwards;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-ping,
          .animate-fade-in,
          .animate-modal-in,
          .animate-modal-content {
            animation: none;
          }
        }
      `}</style>
    </>
  );
}

export default GalleryPage;
